import moment from "moment/moment";
import { DEX_TRADE_TYPE as TradeType } from "@/constants/dex.ts";
import { TradeTableData } from "@/pages/SharkDex/TabContent/Trade/TradeTable.tsx";
import { StakeData } from "@/types";

export function convertNumberIntoFormat(number: number): string {
  number = Number(number);
  if (number < 1e3) {
    return formatNumberByFrac(number); // for numbers less than 1000, return the number itself
  } else if (number >= 1e3 && number < 1e6) {
    return (Math.floor((number / 1e3) * 100) / 100).toFixed(2) + "k"; // for thousands
  } else if (number >= 1e6 && number < 1e9) {
    return (Math.floor((number / 1e6) * 100) / 100).toFixed(2) + "M"; // for millions
  } else if (number >= 1e9 && number < 1e12) {
    return (Math.floor((number / 1e9) * 100) / 100).toFixed(2) + "B"; // for billions
  } else if (number >= 1e12) {
    return (Math.floor((number / 1e12) * 100) / 100).toFixed(2) + "T"; // for trillions
  }

  return number.toString();
}

export const formatNumberByFrac = (
  num: number | undefined,
  fixedCount: number = 2,
): string => {
  if (num === undefined) return "0";

  const threshold = 0.01;
  const minThreshold = 0.000001;
  num = parseFloat(num.toString());

  const getFixedNum = (num: number, fixedCount: number): string => {
    const multipleValue = 10 ** fixedCount;
    return (Math.round(num * multipleValue) / multipleValue).toString();
  };

  if (
    Number.isInteger(num) ||
    (Math.abs(num) < threshold && Math.abs(num) > minThreshold)
  ) {
    const lengthAfterDecimal = Math.ceil(Math.log10(1 / num));
    if (num > 0 && lengthAfterDecimal > 0) {
      return getFixedNum(num, lengthAfterDecimal + 2);
    }
  }

  return getFixedNum(num, fixedCount);
};

export const formatNumberToLocalString = (
  num: number | undefined,
  fixedCount: number = 2,
): string => {
  return Number(formatNumberByFrac(num, fixedCount)).toLocaleString();
};

export function toFixedFloat(number: number, precision: number = 2): string {
  number = Number(number);
  return parseFloat(number.toFixed(Math.min(precision, 6))).toString();
}

export const formatPassedTime = (seconds: number) => {
  const days = Math.floor(seconds / 86400);

  if (days > 0) return `${days} day${days > 1 ? "s" : ""}`;

  const hours = Math.floor(seconds / 3600);

  if (hours > 0) return `${hours} hr${hours > 1 ? "s" : ""}`;

  const mins = Math.floor(seconds / 60);

  if (mins > 0) return `${mins} min${mins > 1 ? "s" : ""}`;

  if (seconds < 1) return "now";

  return `${Math.floor(seconds)} secs`;
};

export const formatDate2LocalString = (date: string | Date): string => {
  return moment(date).format("MM/DD HH:mm");
};

export const formattedDate = (date: string | Date) =>
  moment(date).format("MM/DD/YYYY");

export const formattedDateTime = (date: string | Date) =>
  moment(date).format("DD/MM/YYYY HH:mm");

export const convertUTCToLocal = (timestamp: string): string => {
  return moment.utc(timestamp).local().format("D MMM YYYY HH:mm");
};

export const convertUTCToLocalDay = (
  timestamp: string,
  offset: number = 0,
): string => {
  return moment.utc(timestamp).utcOffset(offset).format("D MMM YYYY");
};

export const getLocalTimestamp = (): string => {
  return new Date()
    .toLocaleString("en-GB", { hour12: false })
    .replace(/\//g, "-")
    .replace(/, /g, "_")
    .replace(/:/g, "-");
};

export const convertTradeResponseArrayToTableData = (
  tradeResponse: any[],
): TradeTableData[] => {
  return tradeResponse.map((item) => {
    const isPlus = item.tradeType === TradeType.BUY;

    return {
      id: item.id,
      asset: "SHRL",
      userName: item.userName,
      shrlAmount: `${isPlus ? "+ " : "- "}${formatNumberToLocalString(item.shrlAmount)}`,
      avgPrice: formatNumberByFrac(item.avgPrice, 7),
      usdAmount: formatNumberToLocalString(item.usdAmount),
      amountIn:
        item.tradeType == TradeType.BUY
          ? `$${formatNumberToLocalString(item.usdAmount, 2)}`
          : `${formatNumberToLocalString(item.shrlAmount, 2)} SHRL`,
      amountOut:
        item.tradeType == TradeType.SELL
          ? `$${formatNumberToLocalString(item.usdAmount, 2)}`
          : `${formatNumberToLocalString(item.shrlAmount, 2)} SHRL`,
      timeAgo: formatPassedTime(
        Date.now() / 1e3 - Number(new Date(item.createdAt)) / 1e3,
      ), // TODO: if user time is incorrect?
      lastPrice: formatNumberByFrac(item.lastPrice, 7),
      tradeType: item.tradeType,
      tradeTime: new Date(item.createdAt),
      isPlus,
    };
  });
};

export const calculateSlippageByLimitPriceDelta = (
  limitPriceDelta: number,
  currentPrice: number,
): number => {
  if (currentPrice != 0) {
    return roundToPrecision((limitPriceDelta / currentPrice) * 100, 4);
  }

  return 0;
};

export const shrinkTransasctionID = (transactionID: string): string => {
  if (transactionID.length < 10) {
    return transactionID; // Return as-is if too short
  }
  return `${transactionID.slice(0, 6)}...${transactionID.slice(-4)}`;
};

/**
 * Rounds a number to a specific precision but removes trailing zeros.
 * @param num - The number to be rounded.
 * @param precision - The number of decimal places to round to.
 * @returns The rounded number as a string.
 */
export const roundToPrecision = (
  num: number,
  precision: number = 4,
): number => {
  const factor = Math.pow(10, precision);
  const rounded = Math.round(num * factor) / factor;

  // Convert to string to remove trailing zeros and manage precision
  return parseFloat(rounded.toFixed(precision));
};

export function priceScale(tickSize: string | number) {
  if (Number(tickSize) >= 1) {
    return Math.pow(10, Number(tickSize));
  } else {
    return Math.round(1 / parseFloat(String(tickSize)));
  }
}

export function getPoolShareChangePercentage(
  stakeData: StakeData,
  changeAmount: number,
): string {
  const currentPercentage =
    stakeData.totalStakes === 0
      ? 0
      : stakeData.user.yourStake / stakeData.totalStakes;
  const predictPercentage =
    stakeData.totalStakes + changeAmount === 0
      ? 0
      : (stakeData.user.yourStake + changeAmount) /
        (stakeData.totalStakes + changeAmount);
  return formatNumberToLocalString(
    (predictPercentage - currentPercentage) * 100,
  );
}

export function getUnstakePoolShareChangePercentage(
  stakeData: StakeData,
  changeAmount: number,
): string {
  const currentPercentage =
    stakeData.totalStakes === 0
      ? 0
      : stakeData.user.yourStake / stakeData.totalStakes;

  if (currentPercentage <= 0) return "0";

  if (stakeData.totalStakes === changeAmount) return "100";

  const predictPercentage =
    (stakeData.user.yourStake - changeAmount) / stakeData.totalStakes;
  return formatNumberToLocalString(
    (currentPercentage - predictPercentage) * 100,
  );
}
